import {
    AddressType,
    DealerAddressType,
    UploadedDocument,
    UploadedImage
} from '../../common/CommonTypes';

export type TyreSpecificErrors = {
    damagedTyre?: string;
    dateOfIncident?: string;
    damageType?: string;
    repairOption?: string;
    damageTypeComment?: string;
    damageSize?: string;
    closeUpImage?: string;
    distanceImage?: string;
    policeReport?: string;
    invoice?: string;
    crimeReferenceNumber?: string;
    mileageImage?: string;
    registrationImage?: string;
    noPaymentReason?: string;
};

export type TyrePageErrors = {
    tyreType?: string;
    mileage?: string;
    repairOption?: string;
    permissionGranted?: string;
    tyreSpecifics?: TyreSpecificErrors;
    noRepairAddressSelected?: string;
    dateOfIncident?: string;
};

export type TyreRepairRequest = {
    id: number;
    productReference: string;
    name?: string;
    dateOfIncident?: string;
    damagedTyre?: string;
    damageType: string;
    damageTypeComment?: string;
    policeReport?: UploadedDocument;
    invoice?: UploadedDocument;
    uploadedImages: UploadedImage[];
    countryIso?: string;
    crimeReferenceNumber?: string;
    noPaymentReason?: string;
};

export type TyrePageState = {
    mileage?: string | number;
    repairOption?: string;
    tyreRepairRequests: TyreRepairRequest[];
    tyreFormErrors?: TyrePageErrors;
    repairRequestSelectedIndex: number;
    tyreStage: TyreProcessStages;
    repairAddresses: AddressType[];
    garageAddresses?: AddressType[];
    dealerAddress: DealerAddressType;
    repairAddressSelectedIndex: number;
    hasErrors: boolean;
    permissionGranted: boolean;
    currentRepairRequest?: TyreRepairRequest;
    showCancelAlert: boolean;
    updateTyre: string;
    scrollToAddress: boolean;
    isLoadingOnSubmission: boolean;
    dateOfIncident?: string;
};

export type DamageSizeOption = {
    id: string;
    text: string;
    value: string;
    disabled: boolean;
};

export type TyreDamageTypeOption = {
    id: string;
    text: string;
    value: string;
    disabled: boolean;
};

export enum TyreProcessStages {
    General,
    TyreSelection,
    Summary
}

export const ReducerActionNames = {
    UpdateTyreErrors: 'update-tyre-errors',
    UpdateMileage: 'update-mileage',
    UpdateRepairOption: 'update-repair-option',
    UpdateIncidentDate: 'update-incident-date',
    UpdateTyreRepairRequest: 'update-tyre-repair-request',
    UpdateInvoice: 'update-invoice',
    UpdateUploadedImages: 'update-uploaded-images',
    StartNewTyreRequest: 'start-new-tyre-request',
    FetchCurrentTyreRequest: 'fetch-current-tyre-request',
    UpdateTyreStage: 'update-tyre-stage',
    AddTyreRequest: 'add-tyre-request',
    SetPolicyData: 'set-policy-data',
    UpdateRepairAddressSelected: 'set-repair-address-selected',
    UpdateDealerAddress: 'set-dealer-address',
    UpdateGarageAddress: 'set-garage-address',
    SetHasErrors: 'set-has-errors',
    SetPermissionGranted: 'set-permission-granted',
    RemoveTyre: 'remove-selected-tyre',
    SetTyre: 'set-tyre',
    ShowCancelAlert: 'show-alert',
    UpdateTyre: 'update-tyre',
    ScrollToAddress: 'scroll-to-address',
    LoadingOnRequestSubmission: 'set-loading-request-submission',
    UpdateDefaultDealerAddress: 'set-default-dealer-address',
    ClearRepairRequests: 'clear-repair-requests'
} as const;

export const TyreTypes = {
    StandardTyre: 'Standard tyre',
    DiamondCut: 'Diamond cut',
    TwoTone: 'Two tone'
} as const;

export const defaultTyreDamageTypes = {
    FlatTyre: 'Flat tyre',
    BlowOut: 'Blow out'
} as const;

export const nonUSTyreDamageTypes = {
    BlowOut: 'Blow out',
    Puncture: 'Puncture',
    Bulge: 'Bulge',
    SidewallDamage: 'Sidewall damage',
    Malicious: 'Malicious',
    Cuts: 'Cuts',
    Other: 'Other'
} as const;

export const TyrePositions = {
    PassengerFront: 'Passenger Front Tyre',
    DriverFront: 'Driver Front Tyre',
    PassengerRear: 'Passenger Rear Tyre',
    DriverRear: 'Driver Rear Tyre'
} as const;

export const DamageSizes = {
    OneQuarter: '1 Quarter',
    TwoQuarters: '2 Quarters',
    ThreeQuarters: '3 Quarters',
    FourQuarters: '4 Quarters'
} as const;

export const HelpText = {
    TyreType: 'tyre_repair_request.help_text.tyre_type',
    DateOfIncident: 'tyre_repair_request.help_text.date_of_incident',
    TypeofDamage: 'tyre_repair_request.help_text.type_of_damage',
    SizeOfDamage: 'tyre_repair_request.help_text.size_of_damage',
    CloseUp: 'tyre_repair_request.help_text.close_up',
    Distance: 'tyre_repair_request.help_text.distance',
    AdditionalImages: 'tyre_repair_request.help_text.additional_images'
} as const;
